interface FieldDiff {
  label: string;
  canonical: string;
  candidate: string;
  differs: boolean;
}

interface PatientCanonical {
  name?: { first?: string; middle?: string; last?: string };
  gender?: string;
  birthDate?: string;
  telecom?: Array<{ system: string; value: string; use?: string }>;
  address?: Array<{ line?: string[]; city?: string; state?: string; postalCode?: string }>;
  sourceEhrPatientId?: string;
}

interface CandidatePtRow {
  ID?: number | string;
  FIRST?: string;
  MIDDLE?: string;
  LAST?: string;
  DOB?: string;
  SEX?: string;
  EMAIL1?: string;
  PHONE_MOBILE?: string;
  PHONE_HOME?: string;
  ADDRESS1?: string;
  CITY?: string;
  STATE?: string;
  ZIP?: string;
  PHARM1?: string;
  [k: string]: unknown;
}

function dobYmdToIso(s: string | undefined): string {
  if (!s) return '';
  // Cerbo stores 'YYYYMMDD'; canonical is 'YYYY-MM-DD'. Normalize candidate for display.
  if (/^\d{8}$/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  return s;
}

function buildDiff(canonical: PatientCanonical, candidate: CandidatePtRow | null): FieldDiff[] {
  const c = candidate ?? {};
  const phones = (canonical.telecom ?? []).filter((t) => t.system === 'phone').map((t) => t.value);
  const email = (canonical.telecom ?? []).find((t) => t.system === 'email')?.value ?? '';
  const addr = (canonical.address ?? [])[0] ?? {};
  const fields: FieldDiff[] = [
    { label: 'First name', canonical: canonical.name?.first ?? '', candidate: c.FIRST ?? '', differs: false },
    { label: 'Middle', canonical: canonical.name?.middle ?? '', candidate: c.MIDDLE ?? '', differs: false },
    { label: 'Last name', canonical: canonical.name?.last ?? '', candidate: c.LAST ?? '', differs: false },
    { label: 'DOB', canonical: canonical.birthDate ?? '', candidate: dobYmdToIso(c.DOB), differs: false },
    { label: 'Sex', canonical: canonical.gender ?? '', candidate: c.SEX ?? '', differs: false },
    { label: 'Email', canonical: email, candidate: c.EMAIL1 ?? '', differs: false },
    { label: 'Mobile phone', canonical: phones[0] ?? '', candidate: c.PHONE_MOBILE ?? '', differs: false },
    { label: 'Home phone', canonical: phones[1] ?? '', candidate: c.PHONE_HOME ?? '', differs: false },
    { label: 'Address', canonical: (addr.line ?? [])[0] ?? '', candidate: c.ADDRESS1 ?? '', differs: false },
    { label: 'City', canonical: addr.city ?? '', candidate: c.CITY ?? '', differs: false },
    { label: 'State', canonical: addr.state ?? '', candidate: c.STATE ?? '', differs: false },
    { label: 'ZIP', canonical: addr.postalCode ?? '', candidate: c.ZIP ?? '', differs: false },
    { label: 'Source ID', canonical: canonical.sourceEhrPatientId ?? '', candidate: c.PHARM1 ?? '', differs: false },
  ];
  return fields.map((f) => ({ ...f, differs: f.canonical.trim() !== f.candidate.trim() }));
}

export function CanonicalCandidateDiff({
  canonical,
  candidate,
}: {
  canonical: Record<string, unknown>;
  candidate: Record<string, unknown> | null;
}) {
  const diff = buildDiff(canonical as PatientCanonical, candidate as CandidatePtRow | null);

  return (
    <div className="rounded-md overflow-hidden" style={{ border: '1px solid var(--border)' }}>
      <table className="w-full text-[12px]">
        <thead>
          <tr style={{ background: 'var(--bg-tertiary)' }}>
            <th className="px-2 py-1.5 text-left text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-secondary)' }}>Field</th>
            <th className="px-2 py-1.5 text-left text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--accent-blue)' }}>Source (canonical)</th>
            <th className="px-2 py-1.5 text-left text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-secondary)' }}>Cerbo candidate</th>
          </tr>
        </thead>
        <tbody>
          {diff.map((row) => (
            <tr key={row.label} style={{ background: row.differs ? 'rgba(250,204,21,0.08)' : 'transparent' }}>
              <td className="px-2 py-1 font-mono" style={{ color: 'var(--text-dimmed)' }}>{row.label}</td>
              <td className="px-2 py-1 font-mono" style={{ color: 'var(--text-primary)' }}>{row.canonical || '—'}</td>
              <td className="px-2 py-1 font-mono" style={{ color: row.differs ? 'var(--accent-yellow)' : 'var(--text-primary)' }}>{row.candidate || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
